import { useState, useEffect, useRef } from 'react'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Stage } from 'react-konva'
import { bindActionCreators } from 'redux'
import { connect, Provider, ReactReduxContext } from 'react-redux'

import { Box, withStyles } from '@material-ui/core'

import { getDocument, GlobalWorkerOptions, AnnotationEditorType } from 'pdfjs-dist'

import { PDFSinglePageViewer, PDFFindController, PDFLinkService, EventBus } from 'pdfjs-dist/web/pdf_viewer.mjs'

import Annotations from '@tabeeb/modules/annotations'
import { galleryActions, gallerySelectors } from '@tabeeb/modules/gallery'
import { getIsSnippingToolEnabled } from '@tabeeb/modules/playerToolbar/selectors'

import * as contentViewerActions from '../../actions'
import * as contentViewerSelectors from '../../selectors'

import SnippingTool from '../SnippingTool'

import styles from './styles'

import 'pdfjs-dist/web/pdf_viewer.css'
import './pdf-overrides.css'

GlobalWorkerOptions.workerSrc = '/static/js/pdf.worker.min.mjs'

const CSS_UNITS = 96.0 / 72.0

const PdfViewer = ({
  classes,
  containerRef,
  galleryActions,
  contentViewerActions,
  width,
  height,
  pdfSearchText,
  pdfSearchSelectedPageIndex,
  pdfSearchSelectedMatchIndex,
  pdfURL,
  pdfPageNumber,
  contentScale,
  isLoading,
  isDrawing,
  isSelecting,
  isSnippingToolEnabled,
}) => {
  const [pdfView, setPdfView] = useState(null)
  const [documentLoaded, setDocumentLoaded] = useState(false)
  const [documentUrl, setDocumentUrl] = useState(null)

  const viewerRef = useRef(null)
  const internalContainerRef = useRef(null)
  const usedContainerRef = containerRef || internalContainerRef

  const pdfURLWithoutSas = (pdfURL || '').split('?')[0]
  const standardFontsStaticPath = '/libs/pdf_viewer/fonts/'

  const pdfObjectsRef = useRef({
    pdfDocument: null,
    viewer: null,
    eventBus: null,
    findController: null,
    linkService: null,
    removeFns: [],
  })

  useEffect(() => {
    setPdfView(null)
    setDocumentLoaded(false)
    setDocumentUrl(null)

    let cancelled = false

    const addListener = (ev, fn) => {
      const { eventBus } = pdfObjectsRef.current
      if (!eventBus) {
        return
      }
      eventBus.on(ev, fn)
      pdfObjectsRef.current.removeFns.push(() => eventBus.off(ev, fn))
    }

    ;(async () => {
      if (!viewerRef.current) {
        console.error('PdfViewer: viewerRef not mounted; aborting pdf init')
        return
      }
      if (!usedContainerRef.current) {
        console.error('PdfViewer: containerRef not available; aborting pdf init')
        return
      }

      viewerRef.current.classList.add('pdfViewer')

      const eventBus = new EventBus()
      const linkService = new PDFLinkService({ eventBus })
      const findController = new PDFFindController({
        eventBus,
        linkService,
      })

      const singlePageViewer = new PDFSinglePageViewer({
        eventBus,
        container: usedContainerRef.current,
        viewer: viewerRef.current,
        linkService,
        findController,
        textLayerMode: 2,
        annotationMode: 2,
        annotationEditorMode: AnnotationEditorType.DISABLE,
      })

      pdfObjectsRef.current.eventBus = eventBus
      pdfObjectsRef.current.linkService = linkService
      pdfObjectsRef.current.findController = findController
      pdfObjectsRef.current.viewer = singlePageViewer

      let loadingTask
      try {
        loadingTask = getDocument({
          url: pdfURL,
          standardFontDataUrl: standardFontsStaticPath,
        })
        const pdfDocument = await loadingTask.promise
        if (cancelled) {
          await pdfDocument?.destroy?.()
          return
        }
        pdfObjectsRef.current.pdfDocument = pdfDocument

        linkService.setDocument(pdfDocument)
        linkService.setViewer(singlePageViewer)
        singlePageViewer.setDocument(pdfDocument)

        addListener('pagesinit', function () {
          if (cancelled) {
            return
          }
          setDocumentLoaded(true)
          setDocumentUrl(pdfURLWithoutSas)
          singlePageViewer.currentScaleValue = 'page-fit'
          singlePageViewer.currentPageNumber = pdfPageNumber
          contentViewerActions.resetLoadingInProgress()
        })

        addListener('pagechanging', () => {
          contentViewerActions.setLoadingInProgress()
        })

        addListener('pagerendered', () => {
          contentViewerActions.resetLoadingInProgress()
        })

        addListener('updatefindmatchescount', (data) => {
          let query = data?.source?.state?.query
          if (Array.isArray(query)) {
            query = query.filter(Boolean).join(' ')
          } else if (typeof query !== 'string') {
            query = pdfSearchText || ''
          }

          const searchResult = {
            pdfUrl: pdfURL,
            searchText: query,
            matchIndex: data.matchesCount.current,
            matches: data.source.pageMatches,
            contents: data.source._pageContents,
          }
          galleryActions.updatePdfSearchResults(searchResult)
        })

        setPdfView(singlePageViewer)
      } catch (err) {
        contentViewerActions.resetLoadingInProgress()
      }
    })()

    return () => {
      cancelled = true

      try {
        pdfObjectsRef.current.removeFns.forEach((fn) => fn())
      } catch (e) {
        /* empty */
      }

      try {
        pdfObjectsRef.current.viewer?.cleanup?.()
        pdfObjectsRef.current.viewer?.destroy?.()
      } catch (e) {
        /* empty */
      }
      try {
        pdfObjectsRef.current.pdfDocument?.destroy?.()
      } catch (e) {
        /* empty */
      }

      setPdfView(null)
      setDocumentLoaded(false)
      setDocumentUrl(null)
      galleryActions.clearPdfSearchResults()
      galleryActions.setPdfSearchMatchesCount(0)
      galleryActions.clearSearchText()

      pdfObjectsRef.current = {
        pdfDocument: null,
        viewer: null,
        eventBus: null,
        findController: null,
        linkService: null,
        removeFns: [],
      }
    }
  }, [pdfURLWithoutSas])

  useEffect(() => {
    if (
      pdfView &&
      pdfView._pages &&
      pdfView._pages[pdfPageNumber - 1] &&
      documentLoaded &&
      documentUrl === pdfURLWithoutSas
    ) {
      pdfView.currentPageNumber = pdfPageNumber
      pdfView.currentScale = contentScale / CSS_UNITS
      contentViewerActions.resetLoadingInProgress()
    }
  }, [pdfView, pdfURL, documentUrl, documentLoaded, pdfPageNumber, contentScale])

  useEffect(() => {
    if (documentLoaded && pdfView && pdfSearchText) {
      pdfObjectsRef.current.eventBus.dispatch('find', {
        query: pdfSearchText,
        caseSensitive: false,
        findPrevious: undefined,
        highlightAll: true,
        phraseSearch: true,
      })
    }
  }, [pdfView, documentLoaded, pdfSearchText])

  useEffect(() => {
    const { findController } = pdfObjectsRef.current

    if (findController) {
      const pageIndex = pdfSearchSelectedPageIndex
      const matchIndex = pdfSearchSelectedMatchIndex

      if (
        findController &&
        findController.pageMatches[pageIndex] &&
        findController.pageMatches[pageIndex].length >= matchIndex
      ) {
        findController.selected.pageIdx = pageIndex
        findController.selected.matchIdx = matchIndex
        const pageView =
          pdfObjectsRef.current.viewer?.getPageView?.(pageIndex) || pdfObjectsRef.current.viewer?._pages?.[pageIndex]
        if (pageView && typeof pageView.updateMatches === 'function') {
          pageView.updateMatches()
        }
      }
    }
  }, [pdfSearchSelectedPageIndex, pdfSearchSelectedMatchIndex])

  return (
    <Box className={classNames(classes.wrapper, 'viewer-content')} style={{ opacity: isLoading ? 0 : 1 }}>
      {!containerRef && <Box ref={internalContainerRef} className='pdfViewerContainer' />}
      <Box
        ref={viewerRef}
        className={classNames(classes.viewer, 'pdfViewer', {
          'text-layer-disabled': isDrawing || isSelecting || isSnippingToolEnabled,
        })}
        style={{ minHeight: 1, minWidth: 1 }}
      />
      {documentLoaded && (
        <ReactReduxContext.Consumer>
          {({ store }) => (
            <Stage
              className={classes.annotations}
              style={{ maxWidth: width * contentScale, maxHeight: height * contentScale }}
              scaleX={contentScale}
              scaleY={contentScale}
              width={width * contentScale || 1}
              height={height * contentScale || 1}
            >
              <Provider store={store}>
                <Annotations />
              </Provider>
            </Stage>
          )}
        </ReactReduxContext.Consumer>
      )}
      {documentLoaded && <SnippingTool width={width * contentScale} height={height * contentScale} />}
    </Box>
  )
}

PdfViewer.propTypes = {
  classes: PropTypes.shape({
    annotations: PropTypes.string.isRequired,
    viewer: PropTypes.string.isRequired,
    wrapper: PropTypes.string.isRequired,
  }).isRequired,
  containerRef: PropTypes.object,
  contentScale: PropTypes.number.isRequired,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  pdfURL: PropTypes.string.isRequired,
  pdfPageNumber: PropTypes.number.isRequired,
  pdfSearchSelectedMatchIndex: PropTypes.number,
  pdfSearchSelectedPageIndex: PropTypes.number,
  pdfSearchText: PropTypes.string.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isDrawing: PropTypes.bool.isRequired,
  isSelecting: PropTypes.bool.isRequired,
  isSnippingToolEnabled: PropTypes.bool.isRequired,

  contentViewerActions: PropTypes.shape({
    resetLoadingInProgress: PropTypes.func.isRequired,
    setLoadingInProgress: PropTypes.func.isRequired,
  }).isRequired,
  galleryActions: PropTypes.shape({
    clearPdfSearchResults: PropTypes.func.isRequired,
    updatePdfSearchResults: PropTypes.func.isRequired,
    setPdfSearchMatchesCount: PropTypes.func.isRequired,
    clearSearchText: PropTypes.func.isRequired,
  }).isRequired,
}

const mapStateToProps = (state) => {
  const selectedGalleryItem = gallerySelectors.getSelectedGalleryItem(state)

  return {
    width: selectedGalleryItem.width,
    height: selectedGalleryItem.height,
    pdfURL: selectedGalleryItem.pdfURL,
    pdfSearchText: gallerySelectors.getPdfSearchText(state),
    pdfSearchSelectedMatchIndex: gallerySelectors.getPdfSearchResult(state).matchIndex,
    pdfSearchSelectedPageIndex: gallerySelectors.getPdfSearchResult(state).pageIndex,
    pdfPageNumber: selectedGalleryItem.pdfPageNumber,
    isLoading: contentViewerSelectors.getIsLoading(state),
    isDrawing: state.playerToolbar.drawing.drawingState.isDrawing,
    isSelecting: state.playerToolbar.drawing.drawingState.isSelecting,
    isSnippingToolEnabled: getIsSnippingToolEnabled(state),
    contentScale: contentViewerSelectors.getContentScale(state),
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    contentViewerActions: bindActionCreators(contentViewerActions, dispatch),
    galleryActions: bindActionCreators(galleryActions, dispatch),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(PdfViewer))
